import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Style from './Governance.module.css';
import backButton from '../../assets/backButton.svg';
import LoaderWrapper from '../common/LoaderWrapper/LoaderWrapper';
import ConfirmPopup from '../common/popover/confirmPopup';
import PolicyVersionBanner from './PolicyVersionBanner';
import GovernanceLifecycleModals from './GovernanceLifecycleModals';
import SiteDataControls from './SiteDataControls';
import useGovernanceLifecycle from './useGovernanceLifecycle';
import {
  CONTROL_META, draftMetaOf, withType, editorPath,
  policyIdFromSearch, presetDispositionFromSearch, SITE_DISPOSITIONS, DISPOSITION_SANCTIONED, DISPOSITION_DENIED,
  normalizeDomain, isValidDomain,
} from './GovernanceShared';
import { getEnrolledEmailDomains } from '../../store/entities/customers';
import {
  getGovernancePolicy,
  getGovernanceSite,
  getGovernanceSiteDraft,
  createGovernanceSite,
  createGovernanceSiteDraft,
  updateGovernanceApp,
  updateGovernanceSiteRule,
  approveGovernancePolicy,
  rejectGovernancePolicy,
  publishGovernancePolicy,
  clearCreateSiteError,
} from '../../store/entities/governance';

// ─────────────────────────────────────────────────────────────────────────────
// SiteControlsEditor — the WEB/AI per-site editor ("policy = site"). GovernanceEditor
// dispatches WEB/AI here (DATA still uses DataControlsEditor). It has two modes,
// keyed by the ?policyId= query param:
//   • CREATE (no policyId)  → SiteCreator: domain + disposition + email domains;
//     Save calls createGovernanceSite (server derives the PolicyName, opens the
//     first draft, copies the DATA rule set) then continues in edit mode.
//   • EDIT   (policyId)     → SiteEditor: reuses useGovernanceLifecycle /
//     PolicyVersionBanner / GovernanceLifecycleModals. The first edit lazily opens
//     a re-copying draft (fresh reset of per-rule edits). Rule + metadata edits are
//     staged locally and flushed on Save Draft.
// ─────────────────────────────────────────────────────────────────────────────

// Order-insensitive array equality (used for the email-domain dirty check).
const sameSet = (a, b) => {
  const x = Array.isArray(a) ? a : [];
  const y = Array.isArray(b) ? b : [];
  if (x.length !== y.length) return false;
  const s = new Set(x);
  return y.every((v) => s.has(v));
};

// ── shared metadata form (domain + disposition + conditional-access domains) ──
const SiteMetaForm = ({
  controlType, appDomain, domainEditable, dispositionCode, emailDomains,
  domainOptions, editable, onChangeDomain, onChangeDisposition, onToggleEmailDomain, error,
  disableAllScanning, onToggleDisableAllScanning,
}) => {
  const isAi = controlType === 'AI';
  const isSanctioned = dispositionCode === DISPOSITION_SANCTIONED;
  // Moot for a DENIED site — navigation is already fully blocked before any content
  // script runs, so a scan exemption on it has nothing to exempt. Hiding the
  // checkbox here is defense-in-depth only; the API strips noScanEngines for
  // DENIED regardless of what the UI sends (see PIXMC-6437 decision #4).
  const showScanExemption = dispositionCode !== DISPOSITION_DENIED;
  return (
    <div className={Style.card}>
      <div className={Style.cardHeader}>
        <div className={Style.cardTitle}>{isAi ? 'AI Application' : 'Site'}</div>
        <div className={Style.cardSubtitle}>
          The primary domain and access policy for this {isAi ? 'AI app' : 'site'}.
        </div>
      </div>
      <div className={Style.cardBody} style={{ padding: '18px 22px' }}>
        {error && <div className={Style.errorList} style={{ listStyle: 'none', paddingLeft: 0 }}>{error}</div>}

        <div className={Style.formGroup}>
          <label className={Style.label}>{isAi ? 'App URL' : 'Site / App URL'}</label>
          {domainEditable ? (
            <input
              className={Style.input}
              value={appDomain}
              onChange={(e) => onChangeDomain && onChangeDomain(e.target.value)}
              placeholder={isAi ? 'e.g. chat.mistral.ai' : 'e.g. salesforce.com'}
            />
          ) : (
            <div className={Style.input} style={{ background: '#f5f6f8', color: '#6b7280' }}>{appDomain || '—'}</div>
          )}
          {!domainEditable && (
            <div className={Style.hint}>The domain is the site's identity and cannot be changed after creation.</div>
          )}
        </div>

        <div className={Style.formGroup}>
          <label className={Style.label}>Policy</label>
          <select
            className={Style.select}
            value={dispositionCode}
            disabled={!editable}
            onChange={(e) => onChangeDisposition && onChangeDisposition(e.target.value)}
          >
            {SITE_DISPOSITIONS.map((d) => <option key={d.code} value={d.code}>{d.label}</option>)}
          </select>
          <div className={Style.hint}>
            Only Sanctioned {isAi ? 'apps' : 'sites'} carry per-rule data controls and conditional access;
            Unsanctioned {isAi ? 'apps' : 'sites'} ship the global defaults.
          </div>
        </div>

        {showScanExemption && (
          <div className={Style.formGroup}>
            <label className={Style.caItem}>
              <input
                type="checkbox"
                checked={!!disableAllScanning}
                disabled={!editable}
                onChange={() => onToggleDisableAllScanning && onToggleDisableAllScanning()}
              />
              Disable all extension policies for this {isAi ? 'app' : 'site'}
            </label>
            <div className={Style.hint}>
              Turns off DLP scanning and AI response monitoring entirely for this {isAi ? 'app' : 'site'} — it
              stays fully browsable, but pays zero scanning cost. Use this for pages where scanning has an
              unacceptable performance impact. Credential monitoring and phishing detection are unaffected.
            </div>
          </div>
        )}

        {isSanctioned && (
          <div className={Style.formGroup}>
            <label className={Style.label}>Conditional Access</label>
            <div className={Style.hint}>
              Restrict access to users on one or more enrolled email domains. Select none for no restriction.
            </div>
            {domainOptions.length === 0 ? (
              <div className={Style.caEmpty}>No enrolled email domains — access is unrestricted (None).</div>
            ) : (
              <div className={Style.caList}>
                {domainOptions.map((d) => (
                  <label className={Style.caItem} key={d}>
                    <input
                      type="checkbox"
                      checked={emailDomains.includes(d)}
                      disabled={!editable}
                      onChange={() => onToggleEmailDomain && onToggleEmailDomain(d)}
                    />
                    {d}
                  </label>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const domainOptionsFrom = (enrolled) =>
  Array.from(new Set((enrolled || []).map((d) => d && d.domain).filter(Boolean)));

// PIXMC-6083 §10.4 — the API's ERR_GOV_015 ("A Web/AI site for this domain already
// exists.") reads better here as a short, specific label; anything else falls back to
// whatever desc the server sent.
const mapCreateSiteError = (code, desc) => (code === 'ERR_GOV_015' ? 'Domain already exists.' : (desc || 'Failed to create the site.'));

// ═════════════════════════════════════════════════════════════════════════════
// CREATE mode
// ═════════════════════════════════════════════════════════════════════════════
const SiteCreatorBase = (props) => {
  const { customerId, controlType, history, policy, policyType, enrolledEmailDomains } = props;
  const meta = CONTROL_META[controlType] || CONTROL_META.DATA;

  const [appDomain, setAppDomain] = useState('');
  const [dispositionCode, setDispositionCode] = useState(DISPOSITION_SANCTIONED);
  const [emailDomains, setEmailDomains] = useState([]);
  const [disableAllScanning, setDisableAllScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [errorPopup, setErrorPopup] = useState(null);
  // Guards the create-error effect below against a stale createSiteErrorCode/-Desc
  // left in the store by a PRIOR create attempt (possibly on a different site/mount) —
  // only an attempt started in THIS mount is allowed to pop the error.
  const createAttemptedRef = useRef(false);

  useEffect(() => {
    if (!customerId) return;
    props.getGovernancePolicy(customerId, 'DATA');
    props.getEnrolledEmailDomains(customerId);
    props.clearCreateSiteError();
  }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reacts to the store rather than the resolved createGovernanceSite() promise — the
  // api middleware resolves that promise to undefined on every HTTP error (portal-wide
  // convention; see useGovernanceLifecycle.js), so the specific ERR_GOV_015 desc is only
  // ever observable via this dispatch-driven prop update, never via the awaited result.
  useEffect(() => {
    if (!createAttemptedRef.current || !props.createSiteErrorDesc) return;
    createAttemptedRef.current = false;
    setErrorPopup(mapCreateSiteError(props.createSiteErrorCode, props.createSiteErrorDesc));
    props.clearCreateSiteError();
  }, [props.createSiteErrorCode, props.createSiteErrorDesc]); // eslint-disable-line react-hooks/exhaustive-deps

  // Preview rules = the published DATA rule set (what will be copied on create).
  const dataRules = (policyType === 'DATA' && policy && Array.isArray(policy.rules)) ? policy.rules : [];
  const domainOptions = domainOptionsFrom(enrolledEmailDomains);
  const isSanctioned = dispositionCode === DISPOSITION_SANCTIONED;

  const backToLanding = () => history.push(withType(`/customers/${customerId}/governance`, controlType));

  const toggleEmailDomain = (d) =>
    setEmailDomains((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));

  const handleCreate = async () => {
    const noun = controlType === 'AI' ? 'App URL' : 'Site / App URL';
    if (!appDomain.trim()) { setError(`${noun} is required`); return; }
    // Normalize (accept a URL or bare domain) then validate — mirrors the backend
    // so the same input is accepted on both sides. Send the normalized domain so
    // the stored value is the clean bare host.
    const domain = normalizeDomain(appDomain);
    if (!isValidDomain(domain)) {
      setError(`Enter a valid domain, e.g. ${controlType === 'AI' ? 'chat.openai.com' : 'salesforce.com'}`);
      return;
    }
    setError('');
    setSaving(true);
    createAttemptedRef.current = true;
    try {
      const res = await props.createGovernanceSite(customerId, controlType, {
        appDomain: domain,
        dispositionCode,
        emailDomains: isSanctioned ? emailDomains : [],
        // Valid for SANCTIONED and UNSANCTIONED (asymmetric from emailDomains,
        // which is SANCTIONED-only) — only DENIED strips it, mirroring the API's
        // own stripping rule (PIXMC-6437 decision #1).
        noScanEngines: dispositionCode !== DISPOSITION_DENIED && disableAllScanning ? ['ALL'] : [],
      });
      // On any HTTP error res is undefined (the api middleware's portal-wide
      // convention) — the specific-error effect above shows the real message once the
      // store catches up; nothing further to do here for that case.
      if (res === undefined) return;
      const newPolicyId = res.data && res.data[0] && res.data[0].policyId;
      if (!newPolicyId) { setErrorPopup('The site was not created. Please try again.'); return; }
      // Continue in edit mode on the freshly created draft.
      history.replace(editorPath(customerId, controlType, newPolicyId));
    } finally { setSaving(false); }
  };

  return (
    <div className={Style.page}>
      <div className={Style.gvHeader}>
        <div className={Style.returnLink} onClick={backToLanding}>
          <img width="22px" src={backButton} alt="" />
          <span>{meta.returnLabel}</span>
        </div>
        <h1 className={Style.pageTitle}>{controlType === 'AI' ? 'Add AI App' : 'Add Site'}</h1>
        <p className={Style.pageSubtitle}>
          Set the domain and access policy. On save, this site is created with its own copy of the
          published Data Controls rule set, which you can then tailor.
        </p>
      </div>

      <div className={Style.gvScrollBody}>
        <div className={Style.versionBanner}>
          <div className={Style.versionBannerInfo}>
            <div className={Style.versionBannerTitleRow}>
              <span className={Style.versionBannerTitle}>New {controlType === 'AI' ? 'AI app' : 'site'}</span>
            </div>
            <div className={Style.versionBannerSubtitle}>
              Set the domain and disposition, then click Create to add it and open its draft.
            </div>
          </div>
          <div className={Style.versionBannerActions}>
            <button className={`${Style.btn} ${Style.btnPrimary}`} onClick={handleCreate} disabled={saving}>
              {saving ? 'Creating…' : 'Create'}
            </button>
            <button className={Style.btn} onClick={backToLanding} disabled={saving}>Cancel</button>
          </div>
        </div>

        <SiteMetaForm
          controlType={controlType}
          appDomain={appDomain}
          domainEditable={true}
          dispositionCode={dispositionCode}
          emailDomains={emailDomains}
          domainOptions={domainOptions}
          editable={true}
          error={error}
          onChangeDomain={setAppDomain}
          onChangeDisposition={(code) => {
            setDispositionCode(code);
            if (code !== DISPOSITION_SANCTIONED) setEmailDomains([]);
            if (code === DISPOSITION_DENIED) setDisableAllScanning(false);
          }}
          onToggleEmailDomain={toggleEmailDomain}
          disableAllScanning={disableAllScanning}
          onToggleDisableAllScanning={() => setDisableAllScanning((v) => !v)}
        />

        {dispositionCode !== DISPOSITION_DENIED && (
          <>
            <div className={Style.hint} style={{ margin: '4px 2px 8px' }}>
              These Data Controls rules will be copied to the new site:
            </div>
            {/* Read-only preview of the DATA rules that will be copied. */}
            <SiteDataControls controlType={controlType} rules={dataRules} editable={false} />
          </>
        )}
      </div>

      {errorPopup && (
        <ConfirmPopup
          details={{ icon: '', type: 'error', title: 'Something went wrong', description: errorPopup, buttons: 'O' }}
          onCloseClick={() => setErrorPopup(null)}
        />
      )}
    </div>
  );
};

const SiteCreator = connect(
  (state) => ({
    policy: state.entities.governance.policy,
    policyType: state.entities.governance.policyType,
    enrolledEmailDomains: state.entities.customers.enrolledEmailDomains,
    createSiteErrorCode: state.entities.governance.createSiteErrorCode,
    createSiteErrorDesc: state.entities.governance.createSiteErrorDesc,
  }),
  (dispatch) => ({
    getGovernancePolicy: (customerId, controlType) => Promise.resolve(dispatch(getGovernancePolicy(customerId, controlType))),
    getEnrolledEmailDomains: (customerId) => dispatch(getEnrolledEmailDomains(customerId)),
    createGovernanceSite: (customerId, controlType, body) => Promise.resolve(dispatch(createGovernanceSite(customerId, controlType, body))),
    clearCreateSiteError: () => dispatch(clearCreateSiteError()),
  }),
)(SiteCreatorBase);

// ═════════════════════════════════════════════════════════════════════════════
// EDIT mode
// ═════════════════════════════════════════════════════════════════════════════
const BANNER_LABELS = {
  WEB: {
    newPolicy: 'Change the policy or a rule to start a draft.',
    editing: 'Editing re-copies the current Data Controls rules (resetting any per-rule customizations). Change the policy or a rule to start a draft.',
    rejected: 'Rejected — revise the site policy and resubmit for approval.',
  },
  AI: {
    newPolicy: 'Change the policy or a rule to start a draft.',
    editing: 'Editing re-copies the current Data Controls rules (resetting any per-rule customizations). Change the policy or a rule to start a draft.',
    rejected: 'Rejected — revise the app policy and resubmit for approval.',
  },
};
const MODAL_COPY = {
  rejectPlaceholder: 'e.g. Tighten the site policy before publishing…',
  publishDescription:
    'Publishing makes this the live policy for this site, delivered to the extension. The current published version will be archived. Continue?',
};

const SiteEditorBase = (props) => {
  const {
    customerId, controlType, policyId, history, presetDisposition,
    draft, policy, draftType, policyType, draftLoading, publishing, enrolledEmailDomains,
  } = props;
  const meta = CONTROL_META[controlType] || CONTROL_META.DATA;

  // Only surface slice data tagged with this control type (see the landing guard).
  const forType = (data, dataType) => (dataType == null || dataType === controlType ? data : null);
  const activeDraft = forType(draft, draftType);
  const activePolicy = forType(policy, policyType);

  const { draftExists, draftVersion, policyVersionId, draftStatus, isEditable } = draftMetaOf(activeDraft);

  const [metaState, setMetaState] = useState({ appDomain: '', dispositionCode: DISPOSITION_SANCTIONED, emailDomains: [], disableAllScanning: false });
  const [metaSeed, setMetaSeed] = useState({ appDomain: '', dispositionCode: DISPOSITION_SANCTIONED, emailDomains: [], disableAllScanning: false });
  const [localRules, setLocalRules] = useState([]);
  // The rules as last seeded from the server (source of truth to revert to when the
  // user steps away from SANCTIONED — see setDisposition below).
  const seededRulesRef = useRef([]);

  const isSanctioned = metaState.dispositionCode === DISPOSITION_SANCTIONED;
  const isDenied = metaState.dispositionCode === DISPOSITION_DENIED;
  const rulesEditable = isSanctioned && isEditable;
  const domainOptions = domainOptionsFrom(enrolledEmailDomains);

  const metaDirty =
    metaState.dispositionCode !== metaSeed.dispositionCode ||
    !sameSet(metaState.emailDomains, metaSeed.emailDomains) ||
    metaState.disableAllScanning !== metaSeed.disableAllScanning;
  const rulesDirty = localRules.some((r) => r._dirty);
  const dirty = metaDirty || rulesDirty;

  useEffect(() => {
    if (customerId) props.getEnrolledEmailDomains(customerId);
  }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Site-scoped loaders injected into the shared lifecycle hook (which is written
  // against the (customerId, controlType) signature). policyId rides the closure.
  const getSiteDraft = (cid, ct) => props.getGovernanceSiteDraft(cid, ct, policyId);
  const getSitePolicy = (cid, ct) => props.getGovernanceSite(cid, ct, policyId);

  // Open (or resume) the site's re-copying draft; returns the target version's id,
  // its apps, and its rules (freshly copied when the draft was just created).
  const ensureDraft = async () => {
    if (draftExists) {
      return { policyVersionId, rules: activeDraft.rules || [], apps: activeDraft.apps || [], freshlyCreated: false };
    }
    await props.createGovernanceSiteDraft(customerId, controlType, policyId);
    const res = await props.getGovernanceSiteDraft(customerId, controlType, policyId);
    const d = res?.data?.[0] ?? null;
    if (!d || !d.version) return null;
    return {
      policyVersionId: d.version.PolicyVersionId ?? d.version.policyVersionId,
      rules: d.rules || [],
      apps: d.apps || [],
      freshlyCreated: true,
    };
  };

  // Remap a staged (seeded-from-published) rule to the draft's actual ruleId. When
  // the draft was freshly re-copied the ids differ, so match on the copied-rule
  // business key (scanRule + channel + source + description), consuming each draft
  // row once; otherwise the staged row already carries the draft ruleId.
  const buildRuleRemap = (draftRules) => {
    if (!draftRules) return (r) => r.ruleId;
    const rows = [...draftRules];
    const used = new Set();
    const keyOf = (r) => [r.scanRuleId, r.channelCode || 'ANY', r.sourceCode, r.ruleDescription].join('|');
    return (localRule) => {
      const k = keyOf(localRule);
      const idx = rows.findIndex((d, i) => !used.has(i) && keyOf(d) === k);
      if (idx === -1) return null;
      used.add(idx);
      return rows[idx].ruleId;
    };
  };

  const flushPendingChanges = async () => {
    const ens = await ensureDraft();
    if (!ens || !ens.policyVersionId) return null;
    const vid = ens.policyVersionId;

    const targetAppId = (ens.apps[0] && ens.apps[0].appId) || (activeDraft && activeDraft.apps && activeDraft.apps[0] && activeDraft.apps[0].appId) || null;
    const results = [];

    // Metadata: persist the app row when the disposition/email domains changed, or
    // whenever we just created the draft (so it reflects the staged disposition).
    if ((metaDirty || ens.freshlyCreated) && targetAppId) {
      results.push(await props.updateGovernanceApp(customerId, controlType, targetAppId, {
        policyVersionId: vid,
        appDomain: metaState.appDomain,
        dispositionCode: metaState.dispositionCode,
        emailDomains: isSanctioned ? metaState.emailDomains : [],
        // Valid for SANCTIONED and UNSANCTIONED (asymmetric from emailDomains) —
        // only DENIED strips it, mirroring the API's own stripping rule
        // (PIXMC-6437 decision #1).
        noScanEngines: !isDenied && metaState.disableAllScanning ? ['ALL'] : [],
      }));
    }

    // Rule edits: apply each staged action/enabled change to the draft rule ids.
    // Rules are only ever editable while SANCTIONED (server rejects a rule PUT on a
    // non-SANCTIONED version with ERR_GOV_013); guard here too — belt-and-braces —
    // so a staged edit from a disposition change mid-flight can never be sent.
    // setDisposition already clears staged edits the moment the user leaves
    // SANCTIONED, so in practice dirtyRules is already empty by the time this runs.
    const remap = buildRuleRemap(ens.freshlyCreated ? ens.rules : null);
    const dirtyRules = isSanctioned ? localRules.filter((r) => r._dirty) : [];
    for (const r of dirtyRules) {
      const targetId = remap(r);
      if (!targetId) continue;
      results.push(await props.updateGovernanceSiteRule(customerId, controlType, targetId, {
        policyVersionId: vid, actionCode: r.actionCode, isActive: r.isActive,
      }));
    }

    if (results.some((x) => x === undefined)) return null;
    return vid;
  };

  const lc = useGovernanceLifecycle({
    customerId, controlType, history,
    policy: activePolicy, publishing, policyVersionId,
    flush: flushPendingChanges, dirty, allowInitialSave: false, saveErrorNoun: 'site',
    getGovernanceDraft: getSiteDraft,
    getGovernancePolicy: getSitePolicy,
    approveGovernancePolicy: props.approveGovernancePolicy,
    rejectGovernancePolicy: props.rejectGovernancePolicy,
    publishGovernancePolicy: props.publishGovernancePolicy,
  });

  // Seed metadata + rules once the initial load settles (and after each in-place
  // refresh). An open draft wins over the published version; both hydrate apps[0]
  // + the copied rule set.
  useEffect(() => {
    if (!lc.ready) return;
    const openDraft = (activeDraft && activeDraft.version) ? activeDraft : null;
    const src = openDraft || activePolicy || null;
    const app = src && Array.isArray(src.apps) ? src.apps[0] : null;
    const seed = {
      appDomain: app ? app.appDomain : '',
      dispositionCode: app ? app.dispositionCode : DISPOSITION_SANCTIONED,
      emailDomains: app && Array.isArray(app.emailDomains) ? app.emailDomains : [],
      disableAllScanning: !!(app && Array.isArray(app.noScanEngines) && app.noScanEngines.includes('ALL')),
    };
    // metaSeed always stays the true server value — it's the dirty-check baseline
    // (metaDirty below), never overridden.
    setMetaSeed(seed);
    // Sanction/Deny deep-link (PIXMC-6083 §10.3, D4): only on the very first seed
    // (reseedKey === 0 — never re-applied after a later Save Draft/Submit/Reject
    // reseed, which must reflect whatever the admin actually left it as), and only
    // over a site that's still genuinely UNSANCTIONED (a stray/bookmarked query
    // param must not silently flip an already-decided site), pre-select the
    // disposition the button the admin clicked implies, instead of the server's
    // real UNSANCTIONED value. Diverging from metaSeed here is what makes Save Draft
    // light up immediately — the admin still has to review and explicitly save it.
    const applyPreset = lc.reseedKey === 0 && presetDisposition && seed.dispositionCode === 'UNSANCTIONED';
    setMetaState(applyPreset ? { ...seed, dispositionCode: presetDisposition } : seed);
    const rules = src && Array.isArray(src.rules) ? src.rules : [];
    seededRulesRef.current = rules;
    setLocalRules(rules.map((r) => ({ ...r })));
  }, [lc.ready, lc.reseedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRuleChange = (ruleId, patch) => {
    setLocalRules((prev) => prev.map((r) => (r.ruleId === ruleId ? { ...r, ...patch, _dirty: true } : r)));
  };
  const setDisposition = (code) => {
    setMetaState((m) => ({
      ...m,
      dispositionCode: code,
      emailDomains: code === DISPOSITION_SANCTIONED ? m.emailDomains : [],
      // DENIED moots the scan exemption (navigation is already fully blocked);
      // UNSANCTIONED retains it, asymmetric from emailDomains on purpose.
      disableAllScanning: code === DISPOSITION_DENIED ? false : m.disableAllScanning,
    }));
    // Rules are only editable while SANCTIONED, so any edit staged before switching
    // away is now un-persistable (the server would reject the PUT with ERR_GOV_013)
    // and, for Denied, no longer even visible. Revert to the last-seeded rows
    // (dropping any _dirty flags) so the banner's dirty state — and what Save Draft
    // will actually send — never diverges from reality. Nothing is lost server-side:
    // switching back to Sanctioned re-seeds from the same source on the next flush.
    if (code !== DISPOSITION_SANCTIONED) {
      setLocalRules(seededRulesRef.current.map((r) => ({ ...r })));
    }
  };
  const toggleEmailDomain = (d) =>
    setMetaState((m) => ({ ...m, emailDomains: m.emailDomains.includes(d) ? m.emailDomains.filter((x) => x !== d) : [...m.emailDomains, d] }));
  const toggleDisableAllScanning = () =>
    setMetaState((m) => ({ ...m, disableAllScanning: !m.disableAllScanning }));

  return (
    <div className={Style.page}>
      <div className={Style.gvHeader}>
        <div className={Style.returnLink} onClick={lc.backToLanding}>
          <img width="22px" src={backButton} alt="" />
          <span>{meta.returnLabel}</span>
        </div>
        <h1 className={Style.pageTitle}>
          {meta.title}{metaState.appDomain ? `: ${metaState.appDomain}` : ''}
        </h1>
        <p className={Style.pageSubtitle}>
          Metadata and rules are staged locally. Use the version banner below to save the draft,
          submit it for approval, or publish.
        </p>
        <p className={Style.publishedSubtitle}>{lc.publishedText()}</p>
      </div>

      <div className={Style.gvScrollBody}>
        <PolicyVersionBanner
          statusCode={draftStatus}
          versionNumber={draftVersion ? (draftVersion.VersionNumber ?? draftVersion.versionNumber) : null}
          draftExists={draftExists}
          dirty={dirty}
          allowInitialSave={false}
          onSaveDraft={lc.handleSaveDraft}
          onSubmitForApproval={() => lc.setPopup('approve')}
          onReject={() => lc.setPopup('reject')}
          onPublish={() => lc.setPopup('publish')}
          onCancel={lc.handleCancelClick}
          saving={lc.savingDraft}
          publishing={lc.publishing}
          disabled={lc.isBusy}
          labels={BANNER_LABELS[controlType]}
        />
        <LoaderWrapper loading={!lc.ready || draftLoading} waitForData={false} styles={{ minHeight: '40px' }} isFlex={true}>
          <div className={Style.editorStack}>
            <SiteMetaForm
              controlType={controlType}
              appDomain={metaState.appDomain}
              domainEditable={false}
              dispositionCode={metaState.dispositionCode}
              emailDomains={metaState.emailDomains}
              domainOptions={domainOptions}
              editable={isEditable}
              onChangeDisposition={setDisposition}
              onToggleEmailDomain={toggleEmailDomain}
              disableAllScanning={metaState.disableAllScanning}
              onToggleDisableAllScanning={toggleDisableAllScanning}
            />
            {!isDenied && (
              <SiteDataControls
                controlType={controlType}
                rules={localRules}
                editable={rulesEditable}
                onRuleChange={handleRuleChange}
              />
            )}
          </div>
        </LoaderWrapper>
      </div>

      <GovernanceLifecycleModals
        popup={lc.popup} setPopup={lc.setPopup}
        rejectReason={lc.rejectReason} setRejectReason={lc.setRejectReason}
        errorPopup={lc.errorPopup} setErrorPopup={lc.setErrorPopup}
        isBusy={lc.isBusy} publishing={lc.publishing}
        onCancelConfirmed={lc.handleCancelConfirmed}
        onApproveConfirmed={lc.handleApproveConfirmed}
        onRejectConfirmed={lc.handleRejectConfirmed}
        onPublishConfirmed={lc.handlePublishConfirmed}
        rejectPlaceholder={MODAL_COPY.rejectPlaceholder}
        publishDescription={MODAL_COPY.publishDescription}
      />
    </div>
  );
};

const SiteEditor = connect(
  (state) => ({
    draft: state.entities.governance.draft,
    policy: state.entities.governance.policy,
    draftType: state.entities.governance.draftType,
    policyType: state.entities.governance.policyType,
    draftLoading: state.entities.governance.draftLoading,
    publishing: state.entities.governance.publishing,
    enrolledEmailDomains: state.entities.customers.enrolledEmailDomains,
  }),
  (dispatch) => ({
    getGovernanceSite: (customerId, controlType, policyId) => Promise.resolve(dispatch(getGovernanceSite(customerId, controlType, policyId))),
    getGovernanceSiteDraft: (customerId, controlType, policyId) => Promise.resolve(dispatch(getGovernanceSiteDraft(customerId, controlType, policyId))),
    createGovernanceSiteDraft: (customerId, controlType, policyId) => Promise.resolve(dispatch(createGovernanceSiteDraft(customerId, controlType, policyId))),
    updateGovernanceApp: (customerId, controlType, appId, body) => Promise.resolve(dispatch(updateGovernanceApp(customerId, controlType, appId, body))),
    updateGovernanceSiteRule: (customerId, controlType, ruleId, body) => Promise.resolve(dispatch(updateGovernanceSiteRule(customerId, controlType, ruleId, body))),
    approveGovernancePolicy: (customerId, versionId, controlType) => Promise.resolve(dispatch(approveGovernancePolicy(customerId, versionId, controlType))),
    rejectGovernancePolicy: (customerId, versionId, reason, controlType) => Promise.resolve(dispatch(rejectGovernancePolicy(customerId, versionId, reason, controlType))),
    publishGovernancePolicy: (customerId, versionId, controlType) => Promise.resolve(dispatch(publishGovernancePolicy(customerId, versionId, controlType))),
    getEnrolledEmailDomains: (customerId) => dispatch(getEnrolledEmailDomains(customerId)),
  }),
)(SiteEditorBase);

// ── dispatcher: create vs edit by ?policyId= ──
const SiteControlsEditor = ({ controlType, location, match, history }) => {
  const customerId = match?.params?.id;
  const policyId = policyIdFromSearch(location && location.search);
  if (!policyId) {
    return <SiteCreator customerId={customerId} controlType={controlType} history={history} />;
  }
  return (
    <SiteEditor
      key={`${controlType}:${policyId}`}
      customerId={customerId}
      controlType={controlType}
      policyId={policyId}
      presetDisposition={presetDispositionFromSearch(location && location.search)}
      history={history}
    />
  );
};

export default withRouter(SiteControlsEditor);
